import {ReactNode, useEffect, useRef} from 'react';
import styled from 'styled-components';
import {Modal} from '@ui/molecules/modal';
import {getColorFromTheme} from '@ui/ui-utils';

const Dialog = styled.div`
  width: min(480px, calc(100% - 32px));
  max-height: min(680px, calc(100vh - 48px));
  margin: auto;
  overflow: hidden;
  border: 1px solid ${getColorFromTheme('borderLight')};
  border-radius: 12px;
  background: ${getColorFromTheme('white')};
  box-shadow: 0 16px 48px rgba(30, 30, 47, 0.16);
`;

const DialogHeader = styled.div`
  padding: 18px 20px;
  border-bottom: 1px solid ${getColorFromTheme('borderLight')};
`;

const DialogTitle = styled.h2`
  margin: 0;
  color: ${getColorFromTheme('blackTwo')};
  font: 600 18px/26px Graphik, sans-serif;
`;

const DialogBody = styled.div`
  max-height: 500px;
  padding: 16px 20px;
  overflow-y: auto;
`;

const DialogDescription = styled.div`
  margin: 0;
  color: ${getColorFromTheme('textBody')};
  font: 400 14px/22px Graphik, sans-serif;

  strong {
    color: ${getColorFromTheme('blackTwo')};
    font-weight: 600;
  }
`;

const DialogFooter = styled.div`
  display: flex;
  align-items: center;
  justify-content: flex-end;
  gap: 10px;
  padding: 16px 20px;
  border-top: 1px solid ${getColorFromTheme('borderLight')};
  background: ${getColorFromTheme('backgroundLight')};

  @media (max-width: 480px) {
    button {
      flex: 1;
    }
  }
`;

const DialogButton = styled.button`
  min-height: 42px;
  padding: 10px 16px;
  border: 1px solid ${getColorFromTheme('borderDark')};
  border-radius: 8px;
  outline: none;
  background: ${getColorFromTheme('white')};
  color: ${getColorFromTheme('blackTwo')};
  font: 500 14px/22px Graphik, sans-serif;
  cursor: pointer;

  &:focus-visible {
    border-color: ${getColorFromTheme('purple300')};
    box-shadow: ${getColorFromTheme('purple200')} 0 0 0 4px;
  }

  &:disabled {
    cursor: not-allowed;
    opacity: 0.55;
  }
`;

const ConfirmButton = styled(DialogButton)<{$destructive: boolean}>`
  border-color: ${({$destructive, theme}) =>
    $destructive ? theme.colors.red400 : theme.colors.purple300};
  background: ${({$destructive, theme}) =>
    $destructive ? theme.colors.alwaysWhite : theme.colors.purple300};
  color: ${({$destructive, theme}) =>
    $destructive ? theme.colors.red400 : theme.colors.white};

  &:hover:not(:disabled) {
    border-color: ${({$destructive, theme}) =>
      $destructive ? theme.colors.red400 : theme.colors.warmBlue};
    background: ${({$destructive, theme}) =>
      $destructive ? theme.colors.red100 : theme.colors.warmBlue};
  }
`;

export interface ConfirmationDialogProps {
  open: boolean;
  title: string;
  children: ReactNode;
  confirmLabel: string;
  cancelLabel?: string;
  destructive?: boolean;
  isLoading?: boolean;
  confirmDisabled?: boolean;
  onCancel: () => void;
  onConfirm: () => void;
}

export const ConfirmationDialog = ({
  open,
  title,
  children,
  confirmLabel,
  cancelLabel = 'Cancel',
  destructive = true,
  isLoading = false,
  confirmDisabled = false,
  onCancel,
  onConfirm,
}: ConfirmationDialogProps) => {
  const cancelButtonRef = useRef<HTMLButtonElement>(null);
  const titleId = 'beam-confirmation-dialog-title';
  const descriptionId = 'beam-confirmation-dialog-description';

  const handleCancel = () => {
    if (!isLoading) onCancel();
  };

  useEffect(() => {
    if (!open) return;

    cancelButtonRef.current?.focus();
    const handleEscape = (event: KeyboardEvent) => {
      if (event.key === 'Escape' && !isLoading) onCancel();
    };

    document.addEventListener('keydown', handleEscape);
    return () => document.removeEventListener('keydown', handleEscape);
  }, [isLoading, onCancel, open]);

  return (
    <Modal open={open} onClose={handleCancel} zIndex={1300}>
      <Dialog
        role="alertdialog"
        aria-modal="true"
        aria-labelledby={titleId}
        aria-describedby={descriptionId}>
        <DialogHeader>
          <DialogTitle id={titleId}>{title}</DialogTitle>
        </DialogHeader>
        <DialogBody>
          <DialogDescription id={descriptionId}>{children}</DialogDescription>
        </DialogBody>
        <DialogFooter>
          <DialogButton
            ref={cancelButtonRef}
            type="button"
            disabled={isLoading}
            onClick={handleCancel}>
            {cancelLabel}
          </DialogButton>
          <ConfirmButton
            type="button"
            $destructive={destructive}
            disabled={isLoading || confirmDisabled}
            onClick={onConfirm}>
            {isLoading ? `${confirmLabel}…` : confirmLabel}
          </ConfirmButton>
        </DialogFooter>
      </Dialog>
    </Modal>
  );
};
